import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { salesOrdersApi } from './api';
import type {
  CancelSalesOrderPayload,
  CreateDeliveryPayload,
  CreateInvoicePayload,
  RecordSalesPaymentPayload,
  SalesOrder,
  SalesOrderDocumentPayload,
  SalesOrderListParams,
  SalesOrderListResponse,
  SalesOrderStats,
  SalesOrderStatsParams,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useSalesOrders(params: SalesOrderListParams): UseQueryResult<SalesOrderListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'sales-orders', tenantId, params.page ?? 1, params.limit ?? 20, params.status ?? '', params.search ?? '',
      params.start_date ?? '', params.end_date ?? '', params.location_id ?? '',
      params.customer_name ?? '', params.mobile ?? '', params.so_number ?? '',
    ],
    queryFn: () => salesOrdersApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useSalesOrderStats(params: SalesOrderStatsParams): UseQueryResult<SalesOrderStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['sales-orders', tenantId, 'stats', params.location_id ?? ''],
    queryFn: () => salesOrdersApi.stats(params),
    staleTime: 15_000,
  });
}

export function useSalesOrder(id: string | undefined): UseQueryResult<SalesOrder | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['sales-orders', tenantId, 'detail', id],
    queryFn: () => (id ? salesOrdersApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

// Every mutation touches the list, the KPIs and the detail, so one prefix
// invalidation covers them all.
function useInvalidate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['sales-orders', tenantId] });
}

export function useCreateDelivery() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateDeliveryPayload }) => salesOrdersApi.createDelivery(id, payload),
    // Not awaited: the page's onSuccess must navigate away before the refetch
    // re-renders it with the final status and trips its status guard (SS1-002).
    onSuccess: () => { void invalidate(); },
  });
}

export function useCreateInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateInvoicePayload }) => salesOrdersApi.createInvoice(id, payload),
    // Not awaited: the page's onSuccess must navigate away before the refetch
    // re-renders it with the final status and trips its status guard (SS1-002).
    onSuccess: () => { void invalidate(); },
  });
}

export function useCancelInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, invoiceId }: { id: string; invoiceId: string }) => salesOrdersApi.cancelInvoice(id, invoiceId),
    onSuccess: invalidate,
  });
}

export function useRecordSalesPayment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RecordSalesPaymentPayload }) => salesOrdersApi.recordPayment(id, payload),
    onSuccess: invalidate,
  });
}

export function useAddSalesOrderDocument() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SalesOrderDocumentPayload }) => salesOrdersApi.addDocument(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteSalesOrderDocument() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, docId }: { id: string; docId: string }) => salesOrdersApi.deleteDocument(id, docId),
    onSuccess: invalidate,
  });
}

export function useCancelSalesOrder() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CancelSalesOrderPayload }) => salesOrdersApi.cancel(id, payload),
    onSuccess: invalidate,
  });
}

export function useDownloadProformaPdf() {
  return useMutation({ mutationFn: (id: string) => salesOrdersApi.downloadProforma(id) });
}

export function useDownloadSalesInvoicePdf() {
  return useMutation({
    mutationFn: ({ id, invoiceId }: { id: string; invoiceId: string }) => salesOrdersApi.downloadInvoicePdf(id, invoiceId),
  });
}
